<template>
  <WarningSecurityContactModal
            v-if="WarningSecurityContactModalState"
            :isOpen="WarningSecurityContactModalState"
            :warningList="storeItem.warnings"
            :phoneNumber="storeItem.phone"
            :phoneNumber2="storeItem.phone2 || null"
            :whatsAppNumber="storeItem.whatsapp"
            @close="WarningSecurityContactModalState = false"
          />
  <StoreWarningModal
    :isOpen="warningModalState"
    :warning="currentWarning"
    :storeName="storeTitleText"
    @close="closeWarningModal"
  />
  <Modal 
    v-if="showImagePreview"
    :isOpen="showImagePreview" 
    :changeModalState="() => showImagePreview = false"
    modalTitle=""
    size="2xl"
    :hideHeadersBorder="true"
    rounded="42px"
    :disableScrolling="true"
  >
    <div class="relative w-full h-full flex items-center justify-center bg-black/5 backdrop-blur-sm">
      <img 
        :src="previewImageUrl" 
        alt="" 
        class="w-full h-auto max-h-[80vh] object-contain rounded-lg border border-gray-200 shadow-2xl transition-transform duration-300 hover:scale-[1.02]" 
      />
    </div>
  </Modal>
  <section class="relative">
    <!-- BANNER -->
    <div class="sm:rounded-[0.625rem] overflow-hidden h-[220px] xs:h-[240px] sm:h-[280px] md:h-[300px] lg:h-[330px] relative">
      <!-- Image banner -->
      <div
        v-if="storeItem.background_file_type === 'image'"
        class="store-background-container relative overflow-hidden sm:mt-1 transition-[max-height] duration-1000 ease-in-out delay-0 sm:rounded-[0.625rem] w-full h-full"
      >
        <div class="absolute top-0 left-0 right-0 bottom-0 transition-opacity duration-300 ease-in-out w-full h-full">
          <!-- image -->
          <div tabindex="-1" class="relative w-full h-full">
            <div
              class="flex justify-center items-center content-center overflow-hidden absolute top-0 left-0 right-0 bottom-0 w-full h-full"
            >
              <img
                :src="storeItem.background_file || defaultImg"
                ref="storeHeaderImageRef"
                @error="onImageError"
                @click="openImagePreview(storeItem.background_file || defaultImg)"
                alt="Store background"
                class="store-background-image cursor-pointer w-full h-full"
                style="min-height:100%; min-width:100%;"
              />
            </div>
          </div>
        </div>
      </div>
      <div
        v-else
        id="youtubeVideo"
        class="relative w-full h-0 pb-[50%] xs:pb-[45%] sm:pb-[42%] md:pb-[34%] xl:md:pb-[30%] 3xl:md:pb-[25%]"
      >
        <!-- Youtube video banner -->
        <LiteYouTubeEmbed :id="getYouTubeId" :aspect-width="16" :aspect-height="9" :title="storeTitleText" />
      </div>
    </div>

    <!-- inner container -->
    <div
      class="bg-[#fff] border-t border-t-gray-4 flex flex-col xl:flex-row items-center xl:items-start xl:justify-between relative w-full sm:rounded-[0.688rem] min-h-[180px] p-5 px-3 md:px-5 xl:px-8"
    >
      <!-- #CATEGORIES SIDE -->
      <div class="flex-1 hidden xl:block">
        <!-- categories - desktop -->
        <!-- this section will be shown on mobile in a different order -->
        <StoreSectionTitle :title="$t('user.store_categories')" class="mb-1" />
        <StoreCategoriesSection :list="storeCategoriesList" :finalizedNameKey="finalizedNameKey" />
      </div>
      <!-- #IMAGE -->
      <div class="flex-1 order-1 xl:order-2 mt-6 md:mt-9 xl:mt-0 flex flex-col items-center">
        <!-- store image -->
        <img
          @error="onStoreImageError"
          @click="openImagePreview(storeItem.logo || defaultStoreImg)"
          class="object-cover absolute z-[1] xl:static xl:z-auto left-[50%] translate-x-[-50%] xl:translate-x-0 top-0 mt-[-3.063rem] md:mt-[-4.063rem] xl:mt-[-5.063rem] rounded-full border-[3px] border-white select-none w-[5.738rem] h-[5.738rem] md:w-[7.738rem] md:h-[7.738rem] 3xl:w-[7.938rem] 3xl:h-[7.938rem] cursor-pointer"
          width="140"
          height="140"
          :src="storeItem.logo || defaultStoreImg"
          :alt="storeItem.name"
          draggable="false"
        />
        <!-- Store Phone Number -->
        <div class="flex flex-nowrap absolute bottom-[-17px] left-[50%] translate-x-[-50%] gap-3 xl:gap-4 mb-5">
          <!--phone number #1 -->
          <a
            v-if="storePhone1"
            :href="`tel:${storePhone1}`"
            :style="{ direction: 'ltr' }"
            :title="`Call the ${storeItem.name || ''} store`"
            @click="triggerContactPixelEvent"
            class="hidden xl:flex text-[16.5px] font-bold py-1.5 px-3 sm:px-5 md:px-8 xl:px-9 gap-1.5 flex-nowrap text-white rounded-[22px] bg-primary hover:bg-primary/90 transition-colors duration-150"
          >
            <img width="13" height="13" :src="phoneIcon" class="object-contain" alt />
            <span>{{ storePhone1 }}</span>
          </a>
          <!-- phone number #2 -->
          <a
            v-if="storePhone2"
            :href="`tel:${storePhone2}`"
            :style="{ direction: 'ltr' }"
            :title="`Call the ${storeItem.name || ''} store`"
            @click="triggerContactPixelEvent"
            class="hidden xl:flex text-[16.5px] font-bold py-1.5 px-3 sm:px-5 md:px-8 xl:px-9 gap-1.5 flex-nowrap text-white rounded-[22px] bg-primary hover:bg-primary/90 transition-colors duration-150"
          >
            <img width="13" height="13" :src="phoneIcon" class="object-contain" alt />
            <span>{{ storePhone2 }}</span>
          </a>
        </div>

        <!-- store title -->
        <div
          class="mt-1.5 w-full text-center inline-flex shrink-1 min-w-0 flex-nowrap justify-center gap-2 items-center">
          <!-- verify icon -->
          <VerifiedIcon size="lg" v-if="isVerified"
            appendedInnerClass="w-[1.063rem] h-[1.063rem] sm:w-[1.25rem] sm:h-[1.25rem]" />
          <h1
            :class="`text-[1.188rem] md:text-[1.219rem] 3xl:text-[1.25rem] [text-shadow:_0_0_0_#c5c2c2] capitalize font-bold break-words ellipsis-x2`"
            :style="`${storeTitleColor}; line-height:normal;`">
            {{ storeTitleText }}
          </h1>
          <!-- Warning Icon -->
          <button
            v-if="shouldShowWarningIcon"
            type="button"
            @click.stop="openWarningModal"
            class="flex items-center justify-center w-7 h-7 text-lg leading-none text-yellow-500 hover:text-yellow-600 transition-colors cursor-pointer"
            :title="$t('panel.warning_details') || 'Warning details'"
            aria-label="warning-details"
          >
            ⚠️
          </button>
        </div>
        <!-- Description -->
        <TruncateText :text="storeDescription" :limit="descriptionCharactersLimit"
          class="block mt-1 text-[0.875rem] md:text-[0.906rem] text-center 3xl:text-[0.938rem] font-normal px-1 xl:min-h-[3.125rem]"
          style="overflow-wrap: anywhere;"
          appendedClass="text-[#515151] leading-[1.5]" :addTruncateLabel="true">
          <template #toggle-button="{ isExpanded, toggle }">
            <button
              class="text-primary text-[0.73rem] sm:text-[0.875rem] !outline-none hover:brightness-110 duration-150 ease-linear"
              @click="toggle">
              {{ isExpanded ? $t('general.show_less') : $t('general.show_more') }}
            </button>
          </template>
        </TruncateText>
        <!-- Store Badges -->
        <!-- <div v-if="storeCategoriesList && storeCategoriesList.length > 0" class="flex flex-wrap justify-center gap-2 mt-1">
          <span v-for="category in storeCategoriesList" :key="category.id"
            class="inline-flex items-center px-3 py-1 text-[0.75rem] sm:text-[0.813rem] font-medium rounded-full bg-primary/10 text-primary">
            {{ category[finalizedNameKey] }}
          </span>
        </div> -->
        <div class="flex flex-wrap justify-center gap-2 mt-1 mb-4">
          <div v-if="storehashtagsList && storehashtagsList.length > 0" class="flex flex-wrap justify-center gap-2 mb-2">
            <transition name="hashtag">
              <div v-if="randomHashtag" class="mt-4 flex items-center gap-1 font-medium rounded-full bg-primary/10 text-primary px-2 py-0.5 hashtag-transition">
                <span class="text-[0.75rem] font-medium">{{ randomHashtag?.name }}</span>            
              </div>
            </transition>
          </div>
        </div>
      </div>
      <!-- #STORE CONTACT INFO SIDE -->
      <div class="flex-1 order-3 flex flex-col items-center sm:items-end">
        <div class="xl:max-w-[21.625rem] w-full">
          <div class="w-full">
            <!-- Figures -->
            <div
              class="flex mb-3 flex-wrap xxs:flex-row xxs:flex-nowrap justify-center xxs:justify-start items-center w-full">
              <!-- followers count -->
              <NuxtLink :to="followersLink"
                class="cursor-pointer flex flex-row-reverse xxs:flex-col gap-x-1.5 xxs:gap-x-0 justify-center group text-center p-1 xxs:py-2 px-3 lg:px-4 xxs:border-r-[2px] rtl:xxs:border-l-[2px] rtl:xxs:border-r-0 border-l-[#70707030] border-r-[#70707030]">
                <div class="text-[1rem] md:text-[1.4rem] 3xl:text-[1.463rem] text-black">
                  {{ storeFollowersCount }}
                </div>
                <div class="flex flex-nowrap items-center gap-1">
                  <img :src="usersIcon" class="w-[0.75rem] h-auto object-contain" alt width="12" height="12" />
                  <span
                    class="text-[#989898] group-hover:text-primary transition-colors duration-150 text-[0.775rem] sm:text-[0.85rem] md:text-[0.87rem] lg:text-[0.88rem] 3xl:text-[0.938rem] whitespace-nowrap">
                    {{ $t('user.store.followers') }}
                  </span>
                </div>
              </NuxtLink>
              <!-- Views count -->
              <div
                class="flex flex-row-reverse xxs:flex-col gap-x-1.5 xxs:gap-x-0 justify-center text-center  p-1 xxs:py-2 px-3 lg:px-4 xxs:border-r-[2px] rtl:xxs:border-l-[2px] rtl:xxs:border-r-0 border-l-[#70707030] border-r-[#70707030]">
                <div class="text-[1rem] md:text-[1.4rem] 3xl:text-[1.463rem] text-black">
                  {{ storeViews }}
                </div>
                <div class="flex flex-nowrap items-center gap-1">
                  <img :src="viewsIcon" class="w-[0.75rem] h-auto object-contain" alt width="12" height="12" />
                  <span
                    class="text-[#989898] text-[0.775rem] sm:text-[0.85rem] md:text-[0.87rem] lg:text-[0.88rem] 3xl:text-[0.938rem] whitespace-nowrap">
                    {{ $t('user.store.views') }}
                  </span>
                </div>
              </div>
              <!-- Rating -->
              <div
                class="flex flex-row-reverse xxs:flex-col gap-x-1.5 xxs:gap-x-0 justify-center text-center p-1 xxs:py-2 px-3 lg:px-4">
                <div class="text-[1rem] md:text-[1.4rem] 3xl:text-[1.463rem] text-black">
                  {{ storeRating }}
                </div>
                <div class="flex flex-nowrap items-center gap-1">
                  <img :src="ratingIcon" class="align-middle inline w-[0.75rem] h-auto object-contain" alt width="12"
                    height="12" />
                  <span
                    class="align-middle inline text-[#989898] text-[0.775rem] sm:text-[0.85rem] md:text-[0.87rem] lg:text-[0.88rem] 3xl:text-[0.938rem] whitespace-nowrap">
                    {{ $t('user.store.rating') }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <!-- store following -->
          <div
            class="flex flex-col-reverse xxs:flex-row flex-wrap justify-center xxs:justify-start items-center gap-x-3 gap-y-4 w-full xl:mt-4">
            <FollowUnfollowButton appendedClass="flex-1 justify-self-end" :storeName="storeTitleText"
              :toggleFollowingState="toggleFollowingState" :followed="followed"
              :followingInProgress="followingInProgress" :designId="3" />
            <!-- buttons -->
            <div class="flex flex-wrap items-center gap-1.5">
              <!-- notification bell -->
              <button
                @click="toggleBellNotification"
                :disabled="isTogglingMuteNotification"
                class="bg-[#F6F6F6] p-2 sm:px-2.5 lg:px-3 lg:py-2.5 xl:px-4 rounded-[0.5rem] hover:brightness-95 duration-150 ease-in-out">
                <img v-if="isMuted" :src="bellOffIcon" class="w-[1.1rem] h-[1.1rem] md:w-[1.21rem] md:h-[1.21rem]" alt width="19"
                  height="19" />
                <img v-else :src="bellOnIcon" class="w-[1.1rem] h-[1.1rem] md:w-[1.21rem] md:h-[1.21rem]" alt width="19"
                  height="19" />
              </button>
              <!-- copy page link -->
              <button @click="copyLink"
                class="bg-[#F6F6F6] p-2 sm:px-2.5 lg:px-3 lg:py-2.5 xl:px-4 rounded-[0.5rem] hover:brightness-95 duration-150 ease-in-out">
                <img :src="copyIcon" class="w-[1rem] h-[1rem] md:w-[1.063rem] md:h-[1.063rem]" alt width="17"
                  height="17" />
              </button>
              <!-- share page -->
              <button @click="() => changeShareProductModalState(true)" aria-label="share store"
                class="bg-[#F6F6F6] p-2 sm:px-2.5 lg:px-3 lg:py-2.5 xl:px-4 rounded-[0.5rem] hover:brightness-95 duration-150 ease-in-out">
                <img :src="shareIcon" class="w-[1rem] h-[1rem] md:w-[1.063rem] md:h-[1.063rem]" alt width="17"
                  height="17" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

</template>

<script>
import Qrcode from "qrcode.vue";
import { arabizeNumbers, onImageError, debounceFunc, extractYouTubeId, ROUTESNAMES } from "@/helpers";
import { FollowUnfollowButton, VerifiedIcon } from "@/components";
import StoreCategoriesSection from "./StoreCategoriesSection.vue";
import TruncateText from "@/components/generic/TruncateText.vue";
import defaultImg from "@/assets/images/placeholder(doNotRemove).jpg";
import defaultStoreImg from "@/assets/images/shopping-lo.png";
import phoneIcon from "@/assets/store/call.svg";
import ratingIcon from "@/assets/icons/star_outline.svg";
import { mapState } from 'pinia';
import { useGlobalStore } from "@/store/Modules/global";
import viewsIcon from "@/assets/icons/view_eye.svg";
import usersIcon from "@/assets/icons/users-outline.svg";
import bellOffIcon from "@/assets/icons/bell_off.svg";
import bellOnIcon from "@/assets/icons/bell_on.svg";
import shareIcon from "@/assets/icons/share_black.svg";
import copyIcon from "@/assets/icons/copy.svg";
import WhatsappButton from "@/components/user/WhatsappButton.vue";
import Star from "@/assets/shared/icons/Star.vue";
import IconExclamation from "@/assets/shared/icons/IconExclamation.vue";
import StoreSectionTitle from "./StoreSectionTitle.vue";
import LiteYouTubeEmbed from 'vue-lite-youtube-embed';
import 'vue-lite-youtube-embed/style.css';
import WarningSecurityContactModal from "@/components/user/WarningSecurityContactModal.vue";
import Modal from '@/components/generic/Modal.vue';
import StoreWarningModal from "./StoreWarningModal.vue";

export default {
  data() {
    return {
      isExpanded: false,
      defaultImg,
      defaultStoreImg,
      phoneIcon,
      ratingIcon,
      ROUTESNAMES,
      viewsIcon,
      usersIcon,
      bellOffIcon,
      bellOnIcon,
      shareIcon,
      copyIcon,
      warningModalState: false,
      currentWarning: null,
      minHeight: 200,
      maxHeight: 400,
      resizeObserver: null,
      descriptionCharactersLimit: 200,
      showImagePreview: false,
      previewImageUrl: '',
      currentHashtagIndex: 0,
      hashtagTimer: null,
      showPhoneModal: false,
      WarningSecurityContactModalState: false, // Add this missing property
    }
  },
  props: {
    storeItem: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    triggerContactPixelEvent: {
      type: Function,
      required: true
    },
    changeShareProductModalState: {
      type: Function,
      required: true
    },
    toggleFollowingState: {
      type: Function,
      required: true
    },
    handleViewingStoreInteractionModal: {
      type: Function,
      required: true
    },
    onStoreImageError: {
      type: Function,
      required: true
    },
    finalizedNameKey: {
      type: String,
      required: true,
      default: "name_ar",
    },
    storeId: {
      type: [String, Number],
      required: true,
      default: ""
    },
    isMuted: {
      type: [Boolean, Number],
      required: true,
      default: false
    },
    isTogglingMuteNotification: {
      type: Boolean,
      required: true,
      default: false
    },
    followed: {
      type: [Boolean, Number],
      required: true,
      default: false
    },
    followingInProgress: {
      type: Boolean,
      required: true,
      default: false
    },
    storeDescription: {
      type: String,
      required: true,
      default: ""
    },
    storeCurrentURL: {
      type: String,
      required: true,
      default: ""
    },
    storeCategoriesList: {
      type: Array,
      required: true,
      default: () => ([]),
    },
    toggleBellNotification: {
      type: Function,
      required: true
    }
  },
  methods: {
    onImageError,
    arabizeNumbers,
    ...mapState(useGlobalStore, {
      localStorageCopy: (store) => store.localStorageCopy
    }),
    updateBackgroundHeroImageHeights(height) {
      this.minHeight = height;
      this.maxHeight = height;
    },
    formatCountFigures(num) {
      //we format the figures here to make it readable by the user and to avoid taking much space in the UI for large numbers
      num = +num;
      if (num >= 1_000_000_000) {
        return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
      } else if (num >= 1_000_000) {
        return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
      } else if (num >= 1_000) {
        return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
      } else {
        return num.toString();
      }
    },
    copyLink() {
      navigator.clipboard.writeText(this.storeCurrentURL);
      this.notify({ type: 'info', msg: this.$t('general.link_was_copied') })
    },
    handleWhatsAppClick(){
      this.WarningSecurityContactModalState = true;
    },
    openImagePreview(imageUrl) {
      this.previewImageUrl = imageUrl;
      this.showImagePreview = true;
    },
    rotateHashtags() {
      const hashtags = this.storeItem?.hashtags || [];
      if (!hashtags.length) return;
      
      this.currentHashtagIndex = (this.currentHashtagIndex - 1 + hashtags.length) % hashtags.length;
    },
    startHashtagRotation() {
      this.hashtagTimer = setInterval(() => {
        this.rotateHashtags();
      }, 2000); // Rotate every 2 seconds
    },
    stopHashtagRotation() {
      if (this.hashtagTimer) {
        clearInterval(this.hashtagTimer);
        this.hashtagTimer = null;
      }
    },
    copyToClipboard(number) {
      navigator.clipboard.writeText(number);
      this.notify({ type: 'info', msg: this.$t('general.link_was_copied') });
    },
    openWarningModal() {
      if (this.storeItem?.latest_warning) {
        this.currentWarning = this.storeItem.latest_warning;
        this.warningModalState = true;
      }
    },
    closeWarningModal() {
      this.warningModalState = false;
      this.currentWarning = null;
    },
  },
  computed: {
    storehashtagsList(){
      return this.storeItem?.hashtags;
    },
    storeTitleColor() {
      return `color:${this.storeItem?.background_color || '#000'}`;
    },
    storeTitleText() {
      return this.storeItem?.name;
    },
    storeViews() {
      return this.formatCountFigures(this.storeItem?.views || 0);
    },
    storeFollowersCount() {
      return this.formatCountFigures(this.storeItem?.followers_count || 0);
    },
    storeRating() {
      return +this.storeItem?.ratings_avg || 0;
    },
    storePhone1() {
      return this.storeItem?.phone
    },
    storePhone2() {
      return this.storeItem?.phone2;
    },
    isVerified() {
      return !!this.storeItem?.verified;
    },
    getYouTubeId() {
      return this.storeItem?.background_file ? extractYouTubeId(this.storeItem.background_file) : '';
    },
    isReseller(){
      return useGlobalStore().localStorageCopy?.user?.role == 'reseller';
    },
    isModeratorReseller(){
      return useGlobalStore().localStorageCopy?.user?.role == 'moderator_reseller';
    },
    followersLink(){
      return `${ROUTESNAMES.user.followers}?storeId=${this.storeId}`;
    },
    myStore(){
      return this.storeItem?.my_store;
    },
    followingPermission(){
      return useGlobalStore().localStorageCopy?.user?.permissions?.['followers']?.['get'] == 1 && useGlobalStore().localStorageCopy?.user?.role == 'moderator_reseller';
    },
    randomHashtag() {
      const hashtags = this.storeItem?.hashtags || [];
      if (!hashtags.length) return null;
      return hashtags[this.currentHashtagIndex];
    },
    shouldShowWarningIcon() {
      const warning = this.storeItem?.latest_warning;
      const myStore = this.myStore;
      
      if (!warning || !warning.type) return false;
      
      // For 'first' warning, only show if my_store is true
      if (warning.type === 'first') {
        return myStore === true || myStore === 1;
      }
      
      // For 'second' and 'third' warnings, show for everyone
      return warning.type === 'second' || warning.type === 'third';
    },
  },
  components: {
    WarningSecurityContactModal,
    Star,
    Qrcode,
    IconExclamation,
    FollowUnfollowButton,
    VerifiedIcon,
    WhatsappButton,
    LiteYouTubeEmbed,
    TruncateText,
    StoreSectionTitle,
    StoreCategoriesSection,
    Modal,
    StoreWarningModal,
  },
  mounted() {
    if (process.client) {
      //keeps the store banner image in the right aspect ratio without being stretched or shrunk
      const image = this.$refs.storeHeaderImageRef;
      if (image) {
        // on load
        const { height } = window.getComputedStyle(image);
        this.updateBackgroundHeroImageHeights(height);
        // on resize change
        this.resizeObserver = new ResizeObserver(entries => {
          debounceFunc(() => {
            for (let entry of entries) {
              const { height } = entry.contentRect;
              this.updateBackgroundHeroImageHeights(`${height}px`)
            }
          }, 80)();
        });
        this.resizeObserver?.observe(image);
      }
    }
    this.startHashtagRotation();
  },
  beforeDestroy() {
    this.resizeObserver?.disconnect();
    this.stopHashtagRotation();
  },
}
</script>

<style lang="scss" >
#youtubeVideo {
  .yt-lite{
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    max-height: inherit; 
  }
}

/* Restored background image styling with proper height */
.store-background-image {
  width: 100% !important;
  height: 100% !important;
  // object-fit: cover !important;
  object-position: center !important;
  /* Firefox compatibility */
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
}

/* Container styling */
.store-background-container {
  position: relative;
  width: 100%;
  overflow: hidden;
  /* Set proper height */
  height: 330px;
  min-height: 330px;
}

/* Responsive height adjustments */
@media (max-width: 640px) {
  .store-background-container {
    height: 280px;
    min-height: 280px;
  }
}

/* Mobile first approach for banner container */
.store-background-container {
  /* Mobile default */
  min-height: 200px;
}

@media (min-width: 460px) {
  .store-background-container {
    min-height: 240px;
  }
}

@media (min-width: 641px) and (max-width: 768px) {
  .store-background-container {
    height: 300px;
    min-height: 300px;
  }
}

@media (min-width: 769px) {
  .store-background-container {
    height: 330px;
    min-height: 330px;
  }
}

.hashtag-transition {
  transition: transform 0.3s ease-out, opacity 0.3s ease-out;
}

.hashtag-enter-active,
.hashtag-leave-active {
  transition: all 0.3s ease;
}

.hashtag-enter-from,
.hashtag-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.hashtag-enter-to,
.hashtag-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>